import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/40 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer"
            style={{ color: scrolled ? undefined : '#ffffff' }}
          >
            <span className={scrolled ? 'text-foreground-950' : ''}>
              Hoobuy<span className={scrolled ? 'text-primary-500' : ''} style={{ color: scrolled ? undefined : '#a8e6cf' }}>Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Inicio
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Categorias
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 rounded-xl bg-background-50 border border-background-200/70 overflow-hidden transition-all duration-200 z-50">
                  <div className="py-1 max-h-[480px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artigo/guia-completo-hoobuyspreadsheet"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Guia de Compras
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Kaspreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-white"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Primary Links */}
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-line text-primary-600"></i>
              </span>
              Inicio
            </Link>

            <Link
              to="/artigo/guia-completo-hoobuyspreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl whitespace-nowrap"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-book-open-line text-accent-600"></i>
              </span>
              Guia de Compras
            </Link>

            {/* Categories Toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100">
                  <i className="ri-grid-line text-secondary-600"></i>
                </span>
                Categorias
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-4 space-y-0.5 border-l-2 border-primary-100 ml-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg cursor-pointer whitespace-nowrap"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-3"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-xl whitespace-nowrap cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-100">
                <i className="ri-external-link-line text-foreground-600"></i>
              </span>
              Kaspreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 mt-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-xl text-center cursor-pointer justify-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}